// src/encounter/lib/care-plan-honest.ts
//
// P0 (Samer, 3 Nov: "The PREVENT card says 'PREVENT not calculated' and 'Goals on target 1/1' at the same time. A card that
// claims success with no calculation behind it is the worst place to lose credibility."). `?x=prevent-honest`.
//
// Two rules, pure, for the care-plan readout (care-plan-readout.tsx) and the patient overview's Progress block:
//   · A goal counts as on target only when the server's 'met' verdict has a result behind it (`latest`). The verdict is
//     computed on read from the latest coded lab result (care-plan-view.ts), so 'met' with no result is not a verdict
//     this screen can show: it reads as "No result yet". The server's own value is never changed, only not claimed.
//   · While the plan's care-model run waits after a calculation that gave no score, the plan shows that state and the
//     inputs the ENGINE names as missing, and no goal tally. The reason comes from the run the overview already reads
//     (care-model-tasks.ts `reasonFor`); with no run read (not a physician, or the read failed), the step's own name
//     saying "not calculated" is enough to withhold the tally, with "Not calculated — see inputs".
import type { CareModelRun } from '@/patients/services/use-care-model-tasks';
import { NOT_CALCULATED_SEE_INPUTS } from '@/patients/lib/calc-not-computed-reason';
import { sameDefinition, type CarePlanView, type GoalStatus, type GoalView } from './care-plan-view';

/** The verdict this screen shows: 'met' only with a result behind it. */
export function shownGoalStatus(goal: GoalView): GoalStatus {
  return goal.status === 'met' && !goal.latest ? 'no-data' : goal.status;
}

/** Goals shown as on target (see `shownGoalStatus`). */
export function goalsOnTarget(goals: readonly GoalView[]): number {
  return goals.filter((g) => shownGoalStatus(g) === 'met').length;
}

export interface PlanNotCalculated {
  /** "Not calculated: <reason>", "Not calculated — see inputs", or the engine's fault line. */
  text: string;
  /** The inputs the engine names as missing; empty when it named none. */
  missingInputs: readonly string[];
}

const NOT_CALCULATED_STEP = /\bnot calculated\b/i;

/**
 * The plan's "not calculated" state, or null. The run is the plan's own: the ONE run of an enrolment of the plan's care-model
 * definition (the key `withCurrentSteps` joins the step on), waiting on that step. Two runs of the definition, or none: no run
 * is borrowed, and only the step's own words decide.
 */
export function planNotCalculated(plan: CarePlanView, runs: readonly CareModelRun[] | undefined): PlanNotCalculated | null {
  if (plan.status !== 'active' || plan.currentStep.state !== 'known') return null;
  const step = plan.currentStep.name;
  const own = (runs ?? []).filter((r) => sameDefinition(plan.definitionId, r.definitionId));
  const run = own.length === 1 && own[0].currentStepName === step ? own[0] : undefined;
  const reason = run?.checklist?.reason;
  if (reason && (reason.kind === 'not-calculated' || reason.kind === 'fault')) {
    return { text: reason.text ?? NOT_CALCULATED_SEE_INPUTS, missingInputs: reason.missingInputs };
  }
  if (NOT_CALCULATED_STEP.test(step)) return { text: NOT_CALCULATED_SEE_INPUTS, missingInputs: [] };
  return null;
}

/** "Not calculated: <reason> · Missing inputs: a, b". */
export function notCalculatedLine(state: PlanNotCalculated): string {
  return state.missingInputs.length > 0 ? `${state.text} · Missing inputs: ${state.missingInputs.join(', ')}` : state.text;
}
